import { useCallback, useEffect, useMemo, useState } from 'react';

import '../App.css';
import { Button, Container, Form, ListGroup, OverlayTrigger, Spinner, Tab, Tabs, Tooltip } from 'react-bootstrap';
import { Skills, sum, add, apply } from './models/Skills';
import { GamePlayedItem, levels, UserData, useUserData, xps } from './models/UserData';
import { SKILLS, SkillTable } from './components/SkillTable';
import { GameDataItem, useGameData } from './models/GameData';
import { MessageAlert, MessageModal } from './components/MessageModal';
import { GameDetailsModal } from './components/GameDetailsModal';
import { GameSearch } from './components/GameSearch';
import { PlayedGameListItem } from './components/PlayedGameListItem';
import { UserProfile } from './components/UserProfile';
import { usePlayData, UserGamePlayedItem } from './models/PlayData';
import { SocialPlayedGameListItem } from './components/SocialGamePlayedListItem';
import { useUserRepository } from './repositories/UserRepository';
import dayjs from 'dayjs';
import { useCollectionLogTracker } from './components/useCollectionLogTracker';

export const LOCAL_STORAGE_SUFFIX = '-2025-06-26';

export interface UserPreferenceData { yearFilter: string };

function PlayTracker() {
    const [userPreferenceData, setUserPreferenceData] = useState<UserPreferenceData>({ yearFilter: new Date().getFullYear().toString() });
    const { listPlays } = useUserRepository();
    const { userData, userDataAllTime, updateUserData, updateProfile, loginProfile, addGame, removeGame, loadingGame } = useUserData({ preferences: userPreferenceData });
    const { gameData, gameLookup } = useGameData();
    const { allPlays, rankedSkillXps, refreshPlayData } = usePlayData({ userData, gameLookup, preferences: userPreferenceData });
    const [selectedGameIndex, setSelectedGameIndex] = useState<number>();
    const [unviewedMessages, setUnviewedMessages] = useState<MessageAlert[]>([]);
    const [prevUserData, setPrevUserData] = useState<UserData>(userData);
    const [loginUserData, setLoginUserData] = useState<string>('');

    const userXps: Skills = useMemo(() => xps(userData), [userData]);

    const addGameToUser = useCallback((gameDataToAdd: GameDataItem, playedOnOverride?: Date): void => {
        addGame(gameDataToAdd, playedOnOverride);
    }, [addGame]);

    const removeSelectedGameFromUser = useCallback(() => {
        removeGame(selectedGameIndex);
        setSelectedGameIndex(undefined);
    }, [removeGame, selectedGameIndex]);

    const addPlayFromSocialFeed = useCallback(async (gamePlayed: UserGamePlayedItem) => {
        const gameDataToAdd = gameLookup[gamePlayed.gameId];

        if (gameDataToAdd) {
            addGameToUser(gameDataToAdd, gamePlayed.playedOn);
        }
    }, [addGameToUser]);

    const { obtainedSkillPets, obtainedCollectionLogItems } = useCollectionLogTracker({
        userData: userDataAllTime,
        onSkillPetObtained: (pet) => {
            setUnviewedMessages((existing) => [...existing, {
                title: <span>🐣 You have a funny feeling like you're being followed...</span>,
                message: <span>You have unlocked the <strong>{SKILLS[pet.skill].name}</strong> skill pet <strong>{pet.name} {pet.icon}</strong>.</span>,
                backgroundcolor: '#E50D27'
            }])
        },
        onCollectionLogItemObtained: (item) => {
            setUnviewedMessages((existing) => [...existing, {
                title: <span>✨ You received a rare drop!</span>,
                message: <span><span style={{ fontSize: '4rem' }}>{item.icon}</span> You have found the rare item <strong>{item.name}</strong>.</span>,
                backgroundcolor: '#E50D27'
            }])
        }
    });

    useEffect(() => {
        const totalLevelChange = sum(levels(userData)) - sum(levels(prevUserData));
        if (totalLevelChange > 0) {
            const updatedLevels = levels(userData);
            const changedLevels = add(updatedLevels, apply(levels(prevUserData), (skillValue) => skillValue * -1))
            for (const [skillString, levelChange] of Object.entries(changedLevels)) {
                const skill = skillString as keyof Skills;
                if (levelChange) {
                    setUnviewedMessages((existing) => [...existing, {
                        title: <span>Congratulations, you just advanced a <strong>{SKILLS[skill].name}</strong> level.</span>,
                        message: <span>{SKILLS[skill].iconLarge} Your <strong>{SKILLS[skill].name}</strong> level is now <strong>{updatedLevels[skill]}</strong>.</span>
                    }])
                }
            }
        }

        setPrevUserData(userData);
    }, [userData, prevUserData]);

    useEffect(() => {
        void (async () => {
            if (userData.status === 'pending') {
                const userPlays = await listPlays({ userId: userData.userId, userSecret: userData.userSecret }) || [];

                const gamesPlayed = [];
                for (const userPlay of userPlays.filter((play) => play.userId === userData.userId)) {
                    const game: GameDataItem = gameLookup[userPlay.gameId] ?? { id: userPlay.gameId, name: "Unknown Game", xp: {} };
                    const gamePlayedItem: GamePlayedItem = { ...game, ...userPlay, playedOn: dayjs(userPlay.playedOn).toDate() }
                    gamesPlayed.push(gamePlayedItem);
                }

                updateUserData({ gamesPlayed, status: 'active' });
            }
        })();
    }, [userData]);

    return (
        <Tabs onSelect={(eventKey) => {
            if (eventKey === 'social' || eventKey === 'skills') {
                refreshPlayData();
            }
        }}>
            <Tab eventKey='skills' title='📊 Skills'>
                <Container className="my-3 mx-auto" style={({ maxWidth: '20rem' })}>
                    <UserProfile value={userData} onChange={updateProfile} />
                    <SkillTable xps={userXps} gameData={gameData} rankedSkillXps={rankedSkillXps} />
                    <div className='my-2' style={{ display: 'block', textAlign: 'center' }}>
                        {[...obtainedSkillPets, ...obtainedCollectionLogItems].map((log, index) => (
                            <OverlayTrigger key={index} overlay={<Tooltip id={log.name}>{log.name}</Tooltip>}>
                                <span>{log.icon}</span>
                            </OverlayTrigger>
                        ))}
                    </div>
                    <GameSearch gameData={gameData} onSubmit={addGameToUser} onFocus={() => refreshPlayData()} recentPlays={allPlays} currentUserId={userData.userId} />
                    <ListGroup className='my-3'>
                        {loadingGame && <PlayedGameListItem disabled key={'loading'} onClick={() => { }} gamePlayed={loadingGame} />}
                        {userData?.gamesPlayed.map((gamePlayed, index) => <PlayedGameListItem key={index} onClick={() => setSelectedGameIndex(index)} gamePlayed={gamePlayed} />)}
                    </ListGroup>
                    <GameDetailsModal show={selectedGameIndex !== undefined} game={selectedGameIndex !== undefined ? userData?.gamesPlayed?.[selectedGameIndex] : undefined} onHide={() => setSelectedGameIndex(undefined)} onGameDelete={removeSelectedGameFromUser} />
                    <MessageModal unviewedMessages={unviewedMessages} onUnviewedMessagesChanged={setUnviewedMessages} />
                </Container>
            </Tab>
            <Tab eventKey='social' title='👨‍👩‍👧‍👦 Game Feed'>
                <Container className="my-3 mx-auto" style={({ maxWidth: '20rem' })}>
                    <ListGroup className='my-3'>
                        {!allPlays.length && <div style={{ textAlign: 'center' }}><p><em>Games played by friends will appear here.</em></p></div>}
                        {allPlays.map((gamePlayed) => (
                            <SocialPlayedGameListItem
                                key={gamePlayed.playId}
                                onClick={() => { }}
                                gamePlayed={gamePlayed}
                                currentUserId={userData.userId}
                                onAddPlay={() => {
                                    void addPlayFromSocialFeed(gamePlayed);
                                }}
                            />
                        ))}
                    </ListGroup>
                </Container>
            </Tab>
            <Tab eventKey='login' title='🔐 Login'>
                <Container className="my-3 mx-auto" style={({ maxWidth: '20rem' })}>
                    <div className="mb-3">{userData.icon} {userData.name ?? "Unknown User"}</div>

                    <Form.Label htmlFor="inputUserKey">User Key</Form.Label>
                    <Form.Control id="inputUserKey" className="mb-3" type='password' maxLength={73} value={loginUserData} onChange={({ target }) => setLoginUserData(target.value)} placeholder="Enter your user key..." />

                    <Button variant="primary" disabled={loginUserData.trim() === ''} onClick={async () => {
                        const userInfo = loginUserData.split('|');
                        if (userInfo.length !== 2) {
                            alert("Invalid user key format. Please use the format: userId|userSecret");
                            return;
                        }
                        loginProfile({ userId: userInfo[0], userSecret: userInfo[1] });
                    }}>
                        Login {userData.status === 'pending' && <Spinner animation="border" role="status" size="sm"></Spinner>}
                    </Button>
                    <Button className='mx-2' variant="danger" onClick={() => {
                        localStorage.clear();
                        window.location.reload();
                    }}>
                        Logout
                    </Button>
                </Container>
            </Tab>
            <Tab eventKey='preferences' title='⚙️ Preferences'>
                <Container className="my-3 mx-auto" style={({ maxWidth: '20rem' })}>
                    <Form.Label htmlFor="inputYearFilter">Show Data From</Form.Label>
                    <Form.Select id="inputYearFilter" className="mb-3" aria-label="Select year filter" value={userPreferenceData.yearFilter} onChange={({ target }) => setUserPreferenceData({ yearFilter: target.value })}>
                        <option value="2026">2026</option>
                        <option value="2025">2025</option>
                        <option value="">All Years</option>
                    </Form.Select>
                </Container>
            </Tab>
        </Tabs>
    );
}

export default PlayTracker;
